import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { redactSensitiveUrls } from './redact';
import { addSourceProperty } from './source';
import { setOperationIdFromCorrelation } from './operationId';

/**
 * Application Insights SDK setup for the Maintenance frontend.
 * Creates and configures the single SDK instance, registers the telemetry
 * initializers (URL redaction, source/screen properties, operation ID from
 * x-correlation-id), and exposes startAppInsights()/isAppInsightsStarted()
 * so the SDK only loads once the enableTelemetry flag allows it. App code
 * sends telemetry through telemetry.ts, not through this instance directly.
 */

// Single App Insights SDK instance shared by the whole app. Configured here,
// but telemetry is not actually sent until AppInsightsGate.tsx calls
// startAppInsights() below, once the enableTelemetry ConfigCat flag resolves.
export const appInsights = new ApplicationInsights({
  config: {
    connectionString: process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING,
    enableAutoRouteTracking: true,
    autoTrackPageVisitTime: true,
    // Only `x-correlation-id` (generated per request in useAxios.ts) is used
    // for frontend/backend correlation — the backend reads that header, not
    // the SDK's own W3C traceparent/Request-Id headers, so those are
    // suppressed entirely rather than sent for no purpose. Cross-service
    // grouping in the Portal instead comes from setOperationIdFromCorrelation
    // below, which forces every telemetry item's Operation ID to be derived
    // from the same x-correlation-id value the backend receives.
    disableCorrelationHeaders: true,
  },
});

appInsights.addTelemetryInitializer(redactSensitiveUrls);
appInsights.addTelemetryInitializer(addSourceProperty);
appInsights.addTelemetryInitializer(setOperationIdFromCorrelation);

let isAppInsightsLoaded = false;

/**
 * Loads the App Insights SDK. Called from AppInsightsGate once the
 * `enableTelemetry` ConfigCat flag resolves truthy. Guarded so a
 * re-render/re-resolve never loads it twice. A missing connection string (or
 * any SDK load failure) only disables telemetry — it must never throw, since
 * that would bubble up to the ErrorBoundary and take down the whole app.
 * @returns true if App Insights is running, false if it could not start
 */
export const startAppInsights = (): boolean => {
  if (isAppInsightsLoaded) {
    return true;
  }

  if (!appInsights.config.connectionString) {
    console.warn(
      'App Insights connection string is not configured; telemetry is disabled.',
    );
    return false;
  }

  try {
    appInsights.loadAppInsights();
    isAppInsightsLoaded = true;
  } catch (error) {
    console.warn('App Insights failed to load; telemetry is disabled.', error);
  }

  return isAppInsightsLoaded;
};

/**
 * Whether startAppInsights() actually loaded the SDK. The track helpers in
 * telemetry.ts check this before every call, and useAxios checks it so the
 * x-correlation-id header is only sent while telemetry is really running (not
 * when the flag is on but the connection string is missing).
 * @returns true once the SDK has loaded successfully
 */
export const isAppInsightsStarted = (): boolean => isAppInsightsLoaded;
